import { useState, useCallback, useRef, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  useSensors,
  useSensor,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  MeasuringStrategy,
} from '@dnd-kit/core';
import type { CollisionDetection, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { usePlannerWeek } from '../../hooks/usePlannerWeek';
import { canScheduleTimelineItem, getPlannerPxPerHour, snapToGrid, minutesToTime, timeToMinutes } from '../../lib/timeline';
import { createTimelineBlock, updateTimelineBlock } from '../../hooks/useTimelineBlocks';
import { queryClient } from '../../api/queryClient';
import { useAppStore } from '../../store';
import { PlannerGrid } from './PlannerGrid';
import { PlannerSidebar } from './PlannerSidebar';

const START_HOUR = 0;
const END_HOUR = 24;
const VISIBLE_START = 8.5;
const VISIBLE_END = 17.5;
const VISIBLE_HOURS = VISIBLE_END - VISIBLE_START;

const priorityBg: Record<string, string> = {
  p1: 'bg-p1/12',
  p2: 'bg-p2/12',
  p3: 'bg-p3/12',
};

const priorityDotClass: Record<string, string> = {
  p1: 'bg-red-400',
  p2: 'bg-amber-400',
  p3: 'bg-blue-400',
};

interface DragItem {
  type: string;
  title: string;
  priority: string;
  scheduledDuration?: number;
  scheduledStartTime?: string;
  itemType?: string;
  parentId?: string | null;
  width?: number;
}

export function PlannerView() {
  const { weekDates } = usePlannerWeek();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const [pxPerHour, setPxPerHour] = useState(() => getPlannerPxPerHour(0, VISIBLE_HOURS));
  const didScrollRef = useRef(false);
  const [activeDrag, setActiveDrag] = useState<DragItem | null>(null);
  const lastPointerY = useRef(0);
  const dragGrabOffsetY = useRef(0);

  // Measure container to compute pxPerHour
  useEffect(() => {
    const el = gridScrollRef.current;
    if (!el) return;

    const measure = () => {
      const stickyHeader = el.querySelector('[data-planner-header]');
      const headerHeight = stickyHeader?.getBoundingClientRect().height ?? 52;
      const availableHeight = el.clientHeight - headerHeight;
      const computed = getPlannerPxPerHour(availableHeight, VISIBLE_HOURS);
      setPxPerHour(computed);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Auto-scroll to 9 AM after the grid has rendered with the correct height
  useEffect(() => {
    if (didScrollRef.current || pxPerHour === 65) return;
    const el = gridScrollRef.current;
    if (!el) return;
    // Wait two frames to ensure React has painted the grid with the correct height
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTop = VISIBLE_START * pxPerHour;
        didScrollRef.current = true;
      });
    });
  }, [pxPerHour]);

  // Track raw pointer position during drag to avoid dnd-kit delta/scroll issues
  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      lastPointerY.current = e.clientY;
    };
    window.addEventListener('pointermove', onPointerMove);
    return () => window.removeEventListener('pointermove', onPointerMove);
  }, []);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );


  const collisionDetection: CollisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return rectIntersection(args);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current;
    if (!data) return;
    document.body.style.cursor = 'grabbing';
    const rect = event.active.rect.current.initial;

    // For planner blocks: compute grab offset (pointer Y - block top edge Y)
    if (data.type === 'planner-block' && rect) {
      dragGrabOffsetY.current = lastPointerY.current - rect.top;
    } else {
      dragGrabOffsetY.current = 0;
    }

    // Get the day column width for the overlay (not the block width which may be subdivided)
    let overlayWidth = rect?.width;
    if (data.type === 'planner-block') {
      const scrollEl = gridScrollRef.current;
      if (scrollEl) {
        const col = scrollEl.querySelector(`[data-date="${data.date}"]`) as HTMLElement;
        if (col) overlayWidth = col.getBoundingClientRect().width - 4;
      }
    }

    setActiveDrag({
      type: data.type as string,
      title: data.title as string,
      priority: data.priority as string,
      scheduledDuration: data.scheduledDuration as number | undefined,
      scheduledStartTime: data.scheduledStartTime as string | undefined,
      itemType: data.itemType as string | undefined,
      parentId: data.parentId as string | null | undefined,
      width: overlayWidth,
    });
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    document.body.style.cursor = '';
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;

    const activeData = active.data.current;
    const overId = over.id as string;

    if (!overId.startsWith('planner-day-')) return;
    const targetDate = overId.replace('planner-day-', '');

    const scrollEl = gridScrollRef.current;
    if (!scrollEl) return;
    const colEl = scrollEl.querySelector(`[data-date="${targetDate}"]`) as HTMLElement;
    if (!colEl) return;

    const dynPxPerHour = Number(colEl.dataset.pxPerHour || 65);
    const colRect = colEl.getBoundingClientRect();
    const pointerY = lastPointerY.current;

    if (activeData?.type === 'sidebar-task') {
      if (!canScheduleTimelineItem({ parentId: activeData.parentId as string | null | undefined })) return;
      // Sidebar items: pointer position = time
      const relativeY = pointerY - colRect.top;
      const minutes = (relativeY / dynPxPerHour) * 60 + START_HOUR * 60;
      const snapped = snapToGrid(minutes);
      const clamped = Math.max(START_HOUR * 60, Math.min(END_HOUR * 60 - 30, snapped));
      const timeStr = minutesToTime(clamped);

      await createTimelineBlock(
        activeData.taskId,
        targetDate,
        timeStr,
        30,
        {
          title: activeData.title,
          priority: activeData.priority,
          itemType: activeData.itemType,
          parentId: activeData.parentId,
        }
      );
      queryClient.invalidateQueries({ queryKey: ['timeline-blocks', 'range'] });
    } else if (activeData?.type === 'planner-block') {
      // Existing blocks: use initial rect top + delta.y to find the overlay's top edge
      const initialRect = active.rect.current.initial;
      if (!initialRect) return;
      const overlayTop = initialRect.top + event.delta.y;
      const relativeY = overlayTop - colRect.top;
      const blockDuration = (activeData.scheduledDuration as number) ?? 30;
      const minutes = (relativeY / dynPxPerHour) * 60 + START_HOUR * 60;
      const snapped = snapToGrid(minutes);
      const clamped = Math.max(START_HOUR * 60, Math.min(END_HOUR * 60 - blockDuration, snapped));
      const timeStr = minutesToTime(clamped);

      await updateTimelineBlock(activeData.blockId, activeData.date, {
        date: targetDate,
        scheduledStartTime: timeStr,
      });
    }
  }, []);

  const handleDragCancel = useCallback(() => {
    document.body.style.cursor = '';
    setActiveDrag(null);
  }, []);

  const handleBlockClick = useCallback((taskId: string) => {
    if (!sidebarOpen) setSidebarOpen(true);
    useAppStore.getState().setHighlightedTaskId(taskId);
    setTimeout(() => {
      useAppStore.getState().setHighlightedTaskId(null);
    }, 1500);
  }, [sidebarOpen]);

  return (
    <div className="flex flex-col h-[calc(100vh-48px)]">
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="flex flex-1 min-h-0">
          <PlannerSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
          <div ref={gridScrollRef} className="flex-1 overflow-y-auto overflow-x-hidden">
            <PlannerGrid
              weekDates={weekDates}
              startHour={START_HOUR}
              endHour={END_HOUR}
              pxPerHour={pxPerHour}
              onBlockClick={handleBlockClick}
            />
          </div>
        </div>

        <DragOverlay dropAnimation={null}>
          {activeDrag && (
            activeDrag.type === 'sidebar-task' ? (
              <div className="flex items-center gap-1.5 py-1.5 px-3 rounded-md bg-bg-raised shadow-lg ring-1 ring-accent/30 cursor-grabbing">
                <span className="text-[12px] text-text-primary font-medium truncate max-w-[200px]">{activeDrag.title}</span>
              </div>
            ) : (() => {
              const duration = activeDrag.scheduledDuration ?? 30;
              const height = Math.max((duration * pxPerHour) / 60, 24);
              const bg = priorityBg[activeDrag.priority] ?? 'bg-p2/12';
              const startMin = activeDrag.scheduledStartTime ? timeToMinutes(activeDrag.scheduledStartTime) : 0;
              const endMin = startMin + duration;
              const startStr = minutesToTime(startMin);
              const endStr = minutesToTime(endMin);
              return (
                <div
                  className={`rounded-md ${bg} shadow-lg ring-1 ring-accent/30 overflow-hidden cursor-grabbing`}
                  style={{ height, width: activeDrag.width ?? 150 }}
                >
                  <div className="px-2 py-1.5">
                    <span className="text-micro font-medium text-text-primary truncate block">
                      {activeDrag.title}
                    </span>
                    <span className="text-micro text-text-muted font-mono">
                      {startStr} – {endStr}
                    </span>
                  </div>
                </div>
              );
            })()
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
